import type { AdminGuide } from "../adminGuides.types";
import { GUIDE_SOURCE_DOCS } from "./sourceDocs";

export const onboardingSupportGuide: AdminGuide = {
  id: "onboarding-support",
  title:
    "Onboarding support: landing → connect provider → payment → finish setup",
  category: "Onboarding",
  audience: ["Support", "CS"],
  summary:
    "Help a customer complete onboarding safely and collect the right evidence when they get stuck.",
  keywords: [
    "onboarding",
    "connect provider",
    "xero",
    "quickbooks",
    "payment",
    "stripe",
    "finished",
    "setup wizard",
    "accounting basis",
  ],
  location: "CustomerApp",
  risk: "medium",
  sourceDocs: [
    GUIDE_SOURCE_DOCS.ONBOARDING_APP,
    GUIDE_SOURCE_DOCS.POSTPAYMENT_UAT,
    GUIDE_SOURCE_DOCS.STRIPE_SUBSCRIPTIONS,
  ],
  warnings: [
    "Do not ask customers to share tokenised URLs, passwords, or payment details.",
    "Prefer troubleshooting on Preview when appropriate (internal workflow).",
  ],
  whatThisHelpsYouDo: [
    "Guide a customer through the expected onboarding steps in the customer app.",
    "Recognise where a customer is stuck and what evidence to collect.",
    "Escalate with the right identifiers (orgId, timestamps, steps).",
  ],
  whenToUse: [
    "A customer is new and needs help completing onboarding.",
    "A customer is stuck connecting Xero/QuickBooks or completing post-payment setup.",
  ],
  whenNotToUse: [
    "A customer request requires Admin overrides (use the override guide).",
    "A customer request is a billing decision (use the Billing & Stripe guide).",
  ],
  beforeYouStart: [
    "Customer email and organisation name (if known).",
    "Which environment they are using (Preview vs Production).",
    "Which provider they are connecting (Xero or QuickBooks).",
    "If applicable: whether they are joining via an invite link (invite code).",
  ],
  steps: [
    "Confirm the customer is on the correct environment (Preview vs Production).",
    "Confirm where they are in onboarding: `/onboarding/landing` → `/onboarding/connect` → `/onboarding/payment` → `/onboarding/finished`.",
    "If stuck at provider connect: ask which provider (Xero/QB) and capture the on-screen error text (if any).",
    "If stuck at payment: use the Billing & Stripe guide to choose Checkout vs Portal troubleshooting.",
    "If they reach `/onboarding/finished`: confirm they can complete the setup wizard steps and reach reports/budget editor.",
    "If they must choose accounting basis: explain cash vs accrual plainly and warn that switching later can trigger a data refresh (see Accounting Method guide).",
    "If blocked, collect: exact URL (no secrets), timestamp (timezone), and a screenshot/screen recording if safe.",
  ],
  whatSuccessLooksLike: [
    "Customer completes onboarding and reaches reports or budget editor.",
    "Provider is connected and the initial sync starts/finishes as expected.",
    "Customer selects the correct accounting basis with correct expectations.",
  ],
  troubleshooting: [
    {
      title: "Customer is stuck after returning from provider OAuth",
      body: [
        "Ask them to refresh once and try again.",
        "Ask what URL path they are on and whether there is an error message.",
        "If it persists, escalate with provider (Xero/QB), timestamp, and organisation/user identifiers.",
      ],
    },
    {
      title: "Provider connect common failures (what to check first)",
      body: [
        "Popup blocked: ask them to allow popups for the site and retry the connect button.",
        "Wrong organisation selected: ask which org name they chose in the provider org picker (if shown).",
        "OAuth error page: capture the exact error text and the time it happened.",
        "Always collect: provider (Xero/QB), environment (Preview/Prod), timestamp (timezone), and the customer org name.",
      ],
    },
    {
      title: "Customer can’t finish the setup wizard",
      body: [
        "Ask which step number they are on and what they see.",
        "If they switched accounting basis, warn that a refresh/re-sync may be required (escalate if unclear).",
      ],
    },
  ],
  escalateWhen: [
    "OAuth/connect loops or fails repeatedly after a refresh and retry.",
    "Payment succeeded but onboarding does not progress after a reasonable time window.",
    "Customer is unsure about accounting basis and the choice is high-impact for their reporting.",
  ],
  relatedGuides: [
    {
      label: "Accounting method (cash vs accrual)",
      guideId: "accounting-method-cash-vs-accrual",
    },
    {
      label: "Billing & Stripe: which link to use",
      guideId: "billing-stripe-which-link",
    },
  ],
  relatedAdminPaths: [
    { label: "Subscription Dashboard", path: "/subscription" },
    { label: "Bug Tracker", path: "/bug-tracker" },
  ],
};
